<script setup>
import { ref, onMounted, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { Button } from '@/components/ui/button'
import { X, Maximize2, Minimize2 } from 'lucide-vue-next'
import { useWindowSize } from '@vueuse/core'

const windowSize = useWindowSize()

// Props
const props = defineProps({
  isFullWidth: {
    type: Boolean,
    default: false
  }
})

// Emits
const emit = defineEmits(['toggle-full-width'])

// Router
const router = useRouter()
const route = useRoute()

// Drawer state and dimensions
const isBig = computed(() => route.meta.layout === 'float-big')
const isFull = computed(() => route.meta.layout === 'full')
const isLargeViewport = computed(() => windowSize.width.value > 1024)
const isShow = ref(true)
const drawerHeight = ref(25)
const isDragging = ref(false)
const headerHeight = ref(0)
let startY = 0

// Get the header height dynamically
onMounted(() => {
  const headerEl = document.querySelector('.drawer-header')
  if (headerEl) {
    headerHeight.value = headerEl.offsetHeight
  }
})

const startDrag = (e) => {
  isDragging.value = true
  startY = e.touches ? e.touches[0].clientY : e.clientY
}

const onDrag = (e) => {
  if (!isDragging.value) return
  const currentY = e.touches ? e.touches[0].clientY : e.clientY
  const diff = startY - currentY
  const newHeight = drawerHeight.value + (diff / window.innerHeight) * 100

  // Calculate minimum height based on header height + some content space
  const minHeight = Math.max(15, (headerHeight.value / window.innerHeight) * 100 + 5)
  // Limit the maximum height to prevent the drawer from going too high
  const maxHeight = 85 // Max 85% of viewport height

  if (newHeight <= maxHeight && newHeight >= minHeight) {
    drawerHeight.value = newHeight
  }
  startY = currentY
}

const endDrag = () => {
  // Ensure drawer doesn't go below minimum height (header + some content)
  const minHeight = Math.max(20, (headerHeight.value / window.innerHeight) * 100 + 10)

  // Ensure drawer doesn't go above 85vh to keep it manageable
  const maxHeight = 85

  if (drawerHeight.value < minHeight) {
    drawerHeight.value = minHeight
  } else if (drawerHeight.value > maxHeight) {
    drawerHeight.value = maxHeight
  }

  isDragging.value = false
}

router.afterEach(() => {
  isShow.value = true
  drawerHeight.value = 25
})

</script>

<template>
  <div
    v-if="route.meta.layout !== 'hide' && isShow && isLargeViewport"
    class="text-sm relative bg-white flex flex-col rounded-md pointer-events-auto transition-all duration-300 h-full"
    :class="{
      'w-full': isFull || isFullWidth,
      'min-w-[540px] max-w-[540px]': isBig && !isFull && !isFullWidth,
      'min-w-[340px] max-w-[340px]': !isBig && !isFull && !isFullWidth
    }"
  >
    <div class="p-1 flex items-center justify-between bg-white shadow-sm rounded-t-md flex-shrink-0">
      <div class="font-bold">
        {{ route.meta.title }}
      </div>
      <div class="flex items-center gap-1">
        <Button
          @click="emit('toggle-full-width')"
          variant="ghost"
          class="w-6 h-6 p-0 rounded-full"
          :title="isFullWidth ? 'Exit full width' : 'Expand to full width'"
        >
          <Minimize2 v-if="isFullWidth" class="w-4 h-4" />
          <Maximize2 v-else class="w-4 h-4" />
        </Button>
        <Button
          @click="isShow = false"
          variant="ghost"
          class="w-6 h-6 p-0 rounded-full"
          title="Close"
        >
          <X class="w-5 h-5" />
        </Button>
      </div>
    </div>
    <div class="flex-1 overflow-y-auto">
      <router-view />
    </div>
  </div>

  <div
    v-if="route.meta.layout !== 'hide' && isShow && !isLargeViewport"
    class="fixed bottom-0 left-0 right-0 bg-white shadow-lg z-50 overflow-hidden pointer-events-auto rounded-t-xl flex flex-col"
    :style="{
      height: `${Math.min(drawerHeight, 85)}vh`,
      maxHeight: '85vh'
    }"
    @mousemove="onDrag"
    @mouseup="endDrag"
    @touchmove="onDrag"
    @touchend="endDrag"
  >
    <div class="drawer-header p-1 shadow-sm z-10 cursor-move flex-shrink-0"
      @mousedown="startDrag"
      @touchstart="startDrag"
    >
      <div class="flex justify-center">
        <div class="w-28 h-1.5 bg-gray-200 rounded mt-1"></div>
      </div>
      <div class="flex items-center justify-between">
        <div class="font-bold pl-1">{{ route.meta.title }}</div>
        <div class="flex items-center gap-1">
          <Button
            @click.stop="emit('toggle-full-width')"
            variant="ghost"
            class="w-6 h-6 p-0 rounded-full"
            :title="isFullWidth ? 'Exit full width' : 'Expand to full width'"
          >
            <Minimize2 v-if="isFullWidth" class="w-4 h-4 text-gray-600" />
            <Maximize2 v-else class="w-4 h-4 text-gray-600" />
          </Button>
          <Button
            @click="isShow = false"
            variant="ghost"
            class="w-6 h-6 p-0 rounded-full"
            title="Close"
          >
            <X class="w-5 h-5 text-gray-600" />
          </Button>
        </div>
      </div>
    </div>

    <div class="flex-1 overflow-y-auto">
      <router-view />
    </div>
  </div>
</template>

<style scoped>
body {
  user-select: none;
}
</style>
